import { getApiErrorStatus, hasApiErrorCode, unwrapApiError } from '@/utils/apiError'

import { POST_STATION_LOCK_BANNER_TEXT } from './constants'

/**
 * Erro do guard de mutação do backend (`PostStationApprovalValidator`) quando o
 * plantão tem avaliação de Pós-Plantão Aprovada (RN-07 / RF-12). O corpo é
 * estruturado: `{ message, code, doctorStationIds }`, com `code` fixo.
 *
 * Extraído de `update.station.modal.vue` para ser reutilizado como defesa em
 * profundidade em outros pontos de mutação (ex.: `management.doctor.modal.vue`)
 * sem duplicar o guard.
 */
export type PostStationApprovedError = {
  response?: {
    status?: number
    data?: {
      message?: string
      code?: string
      /**
       * Ids ofensores. No bloqueio por lote (`assertNotApproved`) é o conjunto
       * completo; no bloqueio por janela de vigência da política de pagamento é
       * uma amostra, e a contagem exata vem em `approvedCount`.
       */
      doctorStationIds?: number[]
      approvedCount?: number
    }
  }
}

// `unknown` (não `PostStationApprovedError`) de propósito: o parâmetro de um
// `catch` é `unknown` sob `strict`, e o guard precisa ser chamável direto no
// caçador de erro em qualquer call site, sem um re-cast antes — a narrowing é
// interna. Retorna `boolean` (e não um type predicate) de propósito: o shape é
// estruturalmente idêntico aos erros locais dos call sites, e o narrowing
// negativo de um predicate colapsaria esses erros para `never`.
// Os helpers canônicos desembrulham `ServiceError` (erro relançado pela store
// com o HTTP original em `originalError`), então o guard também funciona para
// erros que passaram por `Service.handleError`.
export const isPostStationApprovedError = (error: unknown): boolean =>
  getApiErrorStatus(error) === 400 && hasApiErrorCode(error, 'POST_STATION_EVALUATION_APPROVED')

/**
 * Frase a exibir para o bloqueio: a do backend quando ela vem, senão o texto
 * canônico da trava.
 *
 * O backend manda uma mensagem pronta em pt-BR mais específica que a nossa (ela
 * cita o período afetado e a contagem, no caso da política de pagamento), então
 * repassar é melhor que reescrever. O fallback existe porque o `message` não é
 * garantido em toda camada de erro.
 */
export const getPostStationApprovedMessage = (error: unknown): string => {
  const err = unwrapApiError(error) as PostStationApprovedError
  // eslint-disable-next-line no-restricted-syntax -- helper de domínio sobre o erro já desembrulhado
  return err?.response?.data?.message ?? POST_STATION_LOCK_BANNER_TEXT
}

/**
 * Ids de `doctor_station` ofensores, quando o backend os informa. Vazio quando
 * ausentes — é o caso das exclusões por filtro, em que o corpo do erro traz só a
 * contagem.
 */
export const getPostStationApprovedDoctorStationIds = (error: unknown): number[] => {
  const err = unwrapApiError(error) as PostStationApprovedError
  return err?.response?.data?.doctorStationIds ?? []
}
